import { afterFailedRefresh } from '../session';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const REFRESH_STORAGE_KEY = 'focus.refreshToken';
const DEVICE_STORAGE_KEY = 'focus.deviceId';

let accessToken = null;
let refreshPromise = null;
let onSessionExpired = () => {};

export function storeTokens({ accessToken: access, refreshToken }) {
  accessToken = access ?? null;
  if (refreshToken) localStorage.setItem(REFRESH_STORAGE_KEY, refreshToken);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_STORAGE_KEY);
}

export function clearTokens() {
  accessToken = null;
  localStorage.removeItem(REFRESH_STORAGE_KEY);
}

export function onUnauthenticated(handler) {
  onSessionExpired = handler;
}

/** Stable per-install id so the API can list this browser under "My devices". */
/** This browser's device id, so a devices list can point out which row is here. */
export function currentDeviceId() {
  return deviceId();
}

function deviceId() {
  let id = localStorage.getItem(DEVICE_STORAGE_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_STORAGE_KEY, id);
  }
  return id;
}

function deviceLabel() {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : /Firefox\//.test(ua) ? 'Firefox'
    : 'Browser';
  return `${browser} · ${navigator.platform || 'Web'}`;
}

/**
 * What to say when the answer did not come from the API.
 *
 * <p>A proxy sits in front of it and answers some requests itself, with no code and no
 * message of its own worth showing. The status is then all there is to go on, so these
 * are the statuses where "Request failed with status 413" is worse than a sentence.
 */
const STATUS_MESSAGES = {
  // Not a status the server sent: XMLHttpRequest reports a connection that never
  // completed as status 0, and "Request failed with status 0" says nothing. An upload
  // that dies two minutes in is the case this is written for.
  0: 'The upload did not reach the server. Check your connection and try again.',
  413: 'That file is too large to upload.',
  502: 'The server is not responding. Try again in a moment.',
  504: 'The server took too long to answer. Try again in a moment.',
};

export class ApiError extends Error {
  constructor(status, body) {
    super(body?.message || STATUS_MESSAGES[status] || `Request failed with status ${status}`);
    this.status = status;
    this.code = body?.code ?? 'unknown_error';
    this.fieldErrors = body?.fieldErrors ?? [];
  }

  /** Message for a specific form field, when the server reported one. */
  fieldError(name) {
    return this.fieldErrors.find((error) => error.field === name)?.message ?? null;
  }
}

/**
 * The JSON an answer carried, or null when it carried none.
 *
 * <p>Shared with the upload path, which reads its body from XMLHttpRequest rather than
 * from a Response and must not grow a second opinion about what a non-JSON body means.
 */
function parseText(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    // Every answer the API gives is JSON, so this is not one of them: it is the proxy
    // in front of it, answering with an HTML page — a 413 above its body limit, a 502
    // while the backend restarts. That markup used to become the error message and be
    // rendered verbatim in a banner. The status is the only part worth keeping.
    return null;
  }
}

async function parseBody(response) {
  if (response.status === 204) return null;
  return parseText(await response.text());
}

async function rawRequest(path, { method = 'GET', body, formData, auth = true } = {}) {
  const headers = {
    Accept: 'application/json',
    'X-Device-Id': deviceId(),
    'X-Device-Label': deviceLabel(),
  };
  // FormData sets its own multipart boundary, so Content-Type must not be forced.
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth && accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: formData ?? (body === undefined ? undefined : JSON.stringify(body)),
  });
  return { response, payload: await parseBody(response) };
}

/**
 * Spends the stored refresh token for a new pair, at most twice.
 *
 * <p>Twice because of what {@link afterFailedRefresh} describes: rotation revokes the
 * token it is given, and a sibling tab can spend ours between our reading it and our
 * asking. That refusal is not the end of the session — it is a token that moved — so we
 * read storage again and ask once more with what is there. A second refusal is real.
 */
async function rotateRefreshToken() {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const presented = getRefreshToken();
    if (!presented) return null;

    const { response, payload } = await rawRequest('/api/v1/auth/refresh', {
      method: 'POST',
      body: { refreshToken: presented },
      auth: false,
    });
    if (response.ok) {
      storeTokens(payload);
      return payload;
    }
    if (afterFailedRefresh(presented, getRefreshToken()) !== 'retry') return null;
  }
  return null;
}

/**
 * Refreshes at most once per expiry: concurrent callers await the same rotation
 * instead of each burning a refresh token, which would invalidate the others.
 *
 * <p>Resolves to the new auth payload, or null when the session is genuinely over.
 * Null is the only thing that may sign anybody out — see {@link afterFailedRefresh}
 * for why "the server refused us" is not the same answer.
 */
async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = rotateRefreshToken()
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/**
 * Resumes a session from the stored refresh token, and answers with whose it is.
 *
 * <p>Exported because the auth context used to do this itself, with its own call to
 * {@code /auth/refresh}. Two rotation paths meant the one guard against spending a
 * token twice only covered one of them: a reload while another tab was refreshing
 * raced it, and one of the two was refused. There is one path now.
 */
export async function resumeSession() {
  if (!getRefreshToken()) return null;
  const auth = await refreshAccessToken();
  if (!auth) {
    clearTokens();
    return null;
  }
  return auth;
}

/**
 * Ends the session locally and says so in words somebody can act on.
 *
 * <p>The server's own message for an unauthenticated request is "A valid access token
 * is required", which is true and is addressed to a developer. By the time it reaches
 * a banner the person has already been signed out; what they need is to be told that,
 * not the name of the header that was missing.
 */
function sessionEnded() {
  clearTokens();
  onSessionExpired();
  return new ApiError(401, {
    code: 'session_expired',
    message: 'Your session has ended. Sign in again to continue.',
  });
}

/**
 * Fetches a protected binary (the ID proof) and returns an object URL. Callers must
 * revokeObjectURL when done, or the blob is held for the life of the page.
 */
export async function requestBlob(path) {
  const run = async () =>
    fetch(`${BASE_URL}${path}`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });

  let response = await run();
  if (response.status === 401) {
    if (await refreshAccessToken()) response = await run();
    else throw sessionEnded();
  }
  if (!response.ok) throw new ApiError(response.status, await parseBody(response));
  return URL.createObjectURL(await response.blob());
}

/**
 * Saves a protected file the browser cannot fetch on its own — an <a href> carries no
 * Authorization header, so the bytes are fetched here and handed to a temporary link.
 * The filename the server sent in Content-Disposition wins over the fallback.
 */
export async function downloadFile(path, fallbackName) {
  const run = async () =>
    fetch(`${BASE_URL}${path}`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });

  let response = await run();
  if (response.status === 401) {
    if (await refreshAccessToken()) response = await run();
    else throw sessionEnded();
  }
  if (!response.ok) throw new ApiError(response.status, await parseBody(response));

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const named = /filename="?([^";]+)"?/.exec(disposition);
  const url = URL.createObjectURL(await response.blob());
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = named?.[1] ?? fallbackName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Revoked on the next tick: revoking synchronously can cancel the download.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
  return Number(response.headers.get('X-Export-Rows') ?? 0);
}

/**
 * A multipart request that reports how much of the body has gone out.
 *
 * <p>The one place this codebase does not use {@code fetch}. A fetch body is handed to
 * the browser whole and there is no event for how much of it has left: the Streams
 * request body that would give one needs HTTP/2 and is Chromium-only. XMLHttpRequest
 * has had {@code upload.onprogress} for fifteen years, and a 512 MB lesson video is
 * exactly the case where the difference matters — without it the screen says
 * "Uploading…" for four minutes and is indistinguishable from a hang, which is why
 * people cancel and start again.
 *
 * <p>Resolves rather than rejects on a transport failure, so one caller below decides
 * what a status means — including status 0, which is XHR's way of saying the request
 * never completed.
 *
 * @param onProgress called with a partial {loaded, total, sent} as the body goes out,
 *        for the caller to merge over what it already has. The last call is
 *        {sent: true} and nothing else: the last byte is away and the server is still
 *        thinking, which is a different thing to say than 100%, and the size it was
 *        counting towards is still worth showing while it says it.
 */
function sendMultipart(path, { method, formData, onProgress }) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, `${BASE_URL}${path}`);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-Device-Id', deviceId());
    xhr.setRequestHeader('X-Device-Label', deviceLabel());
    // FormData sets its own multipart boundary; Content-Type must not be forced here
    // either, for the same reason the fetch path leaves it alone.
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        onProgress({
          loaded: event.loaded,
          // Left out rather than nulled when the browser cannot say how long the body
          // is: the caller seeds the bar from the file's own size, and that is a better
          // total than none. With neither there is nothing to divide by, and the bar
          // counts bytes instead of showing a percentage stuck at zero.
          ...(event.lengthComputable ? { total: event.total } : {}),
          sent: false,
        });
      };
      xhr.upload.onload = () => onProgress({ sent: true });
    }

    xhr.onload = () => resolve({ status: xhr.status, payload: parseText(xhr.responseText) });
    // Aborted, refused, timed out, DNS gone: all one case here, and none of them has a
    // status. The error above turns that into a sentence.
    xhr.onerror = () => resolve({ status: 0, payload: null });
    xhr.ontimeout = () => resolve({ status: 0, payload: null });
    xhr.onabort = () => resolve({ status: 0, payload: null });

    xhr.send(formData);
  });
}

/**
 * Uploads with progress, refreshing the token once if the request was rejected for an
 * expired one.
 *
 * <p>The retry re-sends the file, which for a large one is not free. It is still the
 * right behaviour, and it is rarer than it looks: the token is checked by the security
 * filter before the body is read, so a token that was valid when the upload started
 * does not expire out from under it — only one that had already expired when the user
 * pressed the button lands here.
 */
async function upload(path, { method = 'POST', formData, onProgress }) {
  let { status, payload } = await sendMultipart(path, { method, formData, onProgress });

  if (status === 401) {
    if (await refreshAccessToken()) {
      ({ status, payload } = await sendMultipart(path, { method, formData, onProgress }));
    } else {
      throw sessionEnded();
    }
  }

  if (status < 200 || status >= 300) throw new ApiError(status, payload);
  return payload;
}

export async function request(path, options = {}) {
  let { response, payload } = await rawRequest(path, options);

  // A 401 on a request that carried a token means the token, not the request: with a
  // stateless JWT there is nothing else it can mean. So it is a refresh, and if that
  // cannot be had, the session is over.
  if (response.status === 401 && options.auth !== false) {
    if (await refreshAccessToken()) {
      ({ response, payload } = await rawRequest(path, options));
    } else {
      throw sessionEnded();
    }
  }

  if (!response.ok) throw new ApiError(response.status, payload);
  return payload;
}

function query(params) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    // Repeated keys for list filters, e.g. status=PENDING&status=UNDER_REVIEW.
    if (Array.isArray(value)) value.forEach((item) => search.append(key, item));
    else search.append(key, value);
  }
  return search.toString();
}

export const api = {
  clientConfig: () => request('/api/v1/meta/client-config', { auth: false }),
  schoolLookup: (q) => request(`/api/v1/schools/lookup?${query({ query: q })}`, { auth: false }),

  // The masters. Unauthenticated because registration needs them before anyone has a
  // token, and because a list of Indian states is not a secret.
  lookupStates: (q) => request(`/api/v1/masters/states?${query({ q })}`, { auth: false }),
  lookupCities: (params) => request(`/api/v1/masters/cities?${query(params)}`, { auth: false }),
  lookupBoards: () => request('/api/v1/masters/boards', { auth: false }),
  lookupSubjects: (q) => request(`/api/v1/masters/subjects?${query({ q })}`, { auth: false }),
  lookupDesignations: () => request('/api/v1/masters/designations', { auth: false }),
  lookupClassLevels: () => request('/api/v1/masters/class-levels', { auth: false }),
  // The classes a book is written for — plain strings, because that is what a title
  // stores. Not the class bands above, which are what a teacher says they handle.
  lookupBookClasses: () => request('/api/v1/masters/book-classes', { auth: false }),
  lookupTitles: () => request('/api/v1/masters/titles', { auth: false }),

  registerStudent: (data) =>
    request('/api/v1/auth/register/student', { method: 'POST', body: data, auth: false }),

  /** Teacher registration is multipart so the ID proof travels with the form. */
  registerTeacher: (payload, idProofFile) => {
    const form = new FormData();
    form.append('payload', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
    if (idProofFile) form.append('idProof', idProofFile);
    return request('/api/v1/auth/register/teacher', { method: 'POST', formData: form, auth: false });
  },

  login: (data) => request('/api/v1/auth/login', { method: 'POST', body: data, auth: false }),
  sendOtp: (data) => request('/api/v1/auth/otp/send', { method: 'POST', body: data, auth: false }),
  verifyOtp: (data) => request('/api/v1/auth/otp/verify', { method: 'POST', body: data, auth: false }),
  forgotPassword: (data) =>
    request('/api/v1/auth/password/forgot', { method: 'POST', body: data, auth: false }),
  resetPassword: (data) =>
    request('/api/v1/auth/password/reset', { method: 'POST', body: data, auth: false }),
  logout: (refreshToken) => request('/api/v1/auth/logout', { method: 'POST', body: { refreshToken } }),

  me: () => request('/api/v1/users/me'),
  changePassword: (data) => request('/api/v1/users/me/password', { method: 'POST', body: data }),
  devices: () => request('/api/v1/users/me/devices'),
  signOutDevice: (id) => request(`/api/v1/users/me/devices/${id}`, { method: 'DELETE' }),

  // Module 10: reached before a session exists, when the device cap refused a sign-in.
  // The password is the proof, so these carry credentials rather than a bearer token.
  devicesForCredentials: (credentials) =>
    request('/api/v1/auth/devices', { method: 'POST', body: credentials, auth: false }),
  signOutDeviceWithCredentials: (id, credentials) =>
    request(`/api/v1/auth/devices/${id}`, { method: 'DELETE', body: credentials, auth: false }),

  // Module 11 — the renewal report is a Sales Rep's screen as well as an Admin's, so
  // the server narrows it to their own schools rather than the client filtering.
  renewals: (params) => request(`/api/v1/admin/renewals?${query(params)}`),
  renewalSummary: () => request('/api/v1/admin/renewals/summary'),
  renewalHistory: (limit = 10) => request(`/api/v1/admin/renewals/history?${query({ limit })}`),
  approveRenewal: (id, body) =>
    request(`/api/v1/sales/renewals/${id}/approve`, { method: 'POST', body }),

  accessLogs: (params) => request(`/api/v1/admin/access-logs?${query(params)}`),
  verifyAccessLog: () => request('/api/v1/admin/access-logs/verify'),
  uploadIdProof: (file) => {
    const form = new FormData();
    form.append('idProof', file);
    return request('/api/v1/users/me/id-proof', { method: 'POST', formData: form });
  },
  teacherStatus: () => request('/api/v1/teacher/status'),

  listSchools: (params) => request(`/api/v1/schools?${query(params)}`),
  getSchool: (id) => request(`/api/v1/schools/${id}`),
  createSchool: (data) => request('/api/v1/schools', { method: 'POST', body: data }),
  updateSchool: (id, data) => request(`/api/v1/schools/${id}`, { method: 'PATCH', body: data }),
  deleteSchool: (id) => request(`/api/v1/schools/${id}`, { method: 'DELETE' }),

  listTitles: (params = {}) => request(`/api/v1/titles?${query(params)}`),
  createTitle: (data) => request('/api/v1/titles', { method: 'POST', body: data }),
  updateTitle: (id, data) => request(`/api/v1/titles/${id}`, { method: 'PATCH', body: data }),
  uploadTitleCover: (id, file) => {
    const form = new FormData();
    form.append('cover', file);
    return request(`/api/v1/titles/${id}/cover`, { method: 'POST', formData: form });
  },
  removeTitleCover: (id) => request(`/api/v1/titles/${id}/cover`, { method: 'DELETE' }),

  toggleAdoption: (data) => request('/api/v1/adoptions', { method: 'PUT', body: data }),
  /**
   * A whole row or column of the Subject × Class grid. Answers with the school's full
   * adoption list, so the grid is redrawn from the server rather than patched from a
   * dozen replies.
   */
  adoptTitles: (schoolId, titleIds, adopted) =>
    request('/api/v1/adoptions/batch', {
      method: 'PUT',
      body: { schoolId, titleIds, adopted },
    }),
  schoolAdoptions: (schoolId) => request(`/api/v1/schools/${schoolId}/adoptions`),
  /** The other question: which schools have taken one title. */
  titleAdoptions: (titleId, params) =>
    request(`/api/v1/titles/${titleId}/adoptions?${query(params)}`),
  myTitles: () => request('/api/v1/teacher/titles'),

  verificationQueue: (params) => request(`/api/v1/verification/teachers?${query(params)}`),
  verificationCounts: () => request('/api/v1/verification/counts'),
  teacherReview: (teacherUserId) => request(`/api/v1/verification/teachers/${teacherUserId}`),
  changeVerificationStatus: (teacherUserId, body) =>
    request(`/api/v1/verification/teachers/${teacherUserId}/status`, { method: 'PATCH', body }),
  assignTeacherSchool: (teacherUserId, schoolId) =>
    request(`/api/v1/verification/teachers/${teacherUserId}/school`, {
      method: 'PATCH',
      body: { schoolId },
    }),
  verificationAuditLog: (teacherUserId) =>
    request(`/api/v1/verification/teachers/${teacherUserId}/audit-log`),
  documentUrl: (documentId) => requestBlob(`/api/v1/documents/${documentId}`),

  generateCodes: (data) => request('/api/v1/admin/codes/generate', { method: 'POST', body: data }),
  listCodes: (params) => request(`/api/v1/admin/codes?${query(params)}`),
  codeBatches: (params) => request(`/api/v1/admin/codes/batches?${query(params)}`),
  exportCodes: (params) =>
    downloadFile(`/api/v1/admin/codes/export?${query(params)}`, 'activation-codes.csv'),
  codeInventory: () => request('/api/v1/sales/codes/inventory'),
  allocateCodes: (data) => request('/api/v1/sales/codes/allocate', { method: 'POST', body: data }),
  schoolCodes: (schoolId) => request(`/api/v1/schools/${schoolId}/codes`),
  myCodes: () => request('/api/v1/teacher/codes'),

  activateCode: (code) =>
    request('/api/v1/student/activate-code', { method: 'POST', body: { code } }),
  studentLibrary: () => request('/api/v1/student/library'),

  // Modules 8 and 9 — the two repositories.
  teacherTree: () => request('/api/v1/teacher/repository/tree'),
  teacherChapters: (titleId) => request(`/api/v1/teacher/books/${titleId}/chapters`),
  teacherResources: (chapterId) => request(`/api/v1/teacher/chapters/${chapterId}/resources`),
  teacherSignedUrl: (resourceId, purpose = 'VIEW') =>
    request(`/api/v1/teacher/resources/${resourceId}/signed-url?${query({ purpose })}`),

  studentChapters: (titleId) => request(`/api/v1/student/books/${titleId}/chapters`),
  studentResources: (chapterId) => request(`/api/v1/student/chapters/${chapterId}/resources`),
  studentStreamUrl: (resourceId) => request(`/api/v1/student/resources/${resourceId}/stream-url`),

  // Loading the repository. Read by the back office, written by the Super Admin.
  /**
   * The shelf the Content Library opens on: every book, retired ones included, with
   * how many chapters and files it holds. Not listTitles, which four other screens
   * read and which carries neither count.
   */
  contentTitles: () => request('/api/v1/admin/content/titles'),
  contentChapters: (titleId) => request(`/api/v1/admin/content/titles/${titleId}/chapters`),
  createChapter: (titleId, data) =>
    request(`/api/v1/admin/content/titles/${titleId}/chapters`, { method: 'POST', body: data }),
  updateChapter: (chapterId, data) =>
    request(`/api/v1/admin/content/chapters/${chapterId}`, { method: 'PATCH', body: data }),
  deleteChapter: (chapterId) =>
    request(`/api/v1/admin/content/chapters/${chapterId}`, { method: 'DELETE' }),
  // Covers sit outside /admin/content because the read is open — see
  // ChapterCoverController. The writes still answer with the chapter's admin row.
  uploadChapterCover: (chapterId, file) => {
    const form = new FormData();
    form.append('cover', file);
    return request(`/api/v1/chapters/${chapterId}/cover`, { method: 'POST', formData: form });
  },
  removeChapterCover: (chapterId) =>
    request(`/api/v1/chapters/${chapterId}/cover`, { method: 'DELETE' }),
  contentResources: (chapterId) => request(`/api/v1/admin/content/chapters/${chapterId}/resources`),
  /**
   * The two calls that carry a lesson file, and the only two that report progress.
   * Everything else here is a form or a list and is over before a bar could paint.
   */
  uploadResource: (chapterId, payload, file, onProgress) => {
    const form = new FormData();
    form.append('payload', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
    form.append('file', file);
    return upload(`/api/v1/admin/content/chapters/${chapterId}/resources`, {
      formData: form,
      onProgress,
    });
  },
  /**
   * New bytes under an existing resource. The id does not change, so a signed link
   * issued in the last few minutes serves the new file — which is the point, and is
   * why the screen asks before calling this.
   */
  replaceResourceFile: (resourceId, file, onProgress) => {
    const form = new FormData();
    form.append('file', file);
    return upload(`/api/v1/admin/content/resources/${resourceId}/file`, {
      method: 'PUT',
      formData: form,
      onProgress,
    });
  },
  updateResource: (resourceId, data) =>
    request(`/api/v1/admin/content/resources/${resourceId}`, { method: 'PATCH', body: data }),
  deleteResource: (resourceId) =>
    request(`/api/v1/admin/content/resources/${resourceId}`, { method: 'DELETE' }),
  /**
   * Pushes a resource's file up to Vimeo, or retries a push that failed. Answers
   * immediately with the resource marked pending — the upload itself runs on the
   * server behind the response, because a 512 MB file does not fit in a request.
   */
  sendResourceToVimeo: (resourceId) =>
    request(`/api/v1/admin/content/resources/${resourceId}/vimeo`, { method: 'POST' }),
  /** Everything still on local storage that belongs on Vimeo, across the catalogue. */
  vimeoBacklog: () => request('/api/v1/admin/content/vimeo/backlog'),
  /** Queues the whole backlog; the pushes run two at a time behind the answer. */
  sendAllToVimeo: () => request('/api/v1/admin/content/vimeo/push-all', { method: 'POST' }),
  refreshVimeoDomains: () =>
    request('/api/v1/admin/content/vimeo/refresh-domains', { method: 'POST' }),

  adminDashboard: () => request('/api/v1/admin/dashboard'),
  supportDashboard: () => request('/api/v1/support/dashboard'),
  salesDashboard: () => request('/api/v1/sales/dashboard'),
  teacherDashboard: () => request('/api/v1/teacher/dashboard'),

  usageReport: (params) => request(`/api/v1/reports/usage?${query(params)}`),
  activationReport: (params) => request(`/api/v1/reports/activation?${query(params)}`),
  exportReport: (kind, params, format) =>
    downloadFile(`/api/v1/reports/${kind}/export?${query({ ...params, format })}`,
      `${kind}-report.${format.toLowerCase()}`),

  // Master administration — the back office, and never a delete: a row other records
  // point at is switched off instead.
  adminListStates: () => request('/api/v1/admin/masters/states'),
  adminCreateState: (data) => request('/api/v1/admin/masters/states', { method: 'POST', body: data }),
  adminUpdateState: (id, data) =>
    request(`/api/v1/admin/masters/states/${id}`, { method: 'PUT', body: data }),
  adminSetStateActive: (id, active) =>
    request(`/api/v1/admin/masters/states/${id}/active`, { method: 'PATCH', body: { active } }),

  adminListCities: (params) => request(`/api/v1/admin/masters/cities?${query(params)}`),
  adminCreateCity: (data) => request('/api/v1/admin/masters/cities', { method: 'POST', body: data }),
  adminUpdateCity: (id, data) =>
    request(`/api/v1/admin/masters/cities/${id}`, { method: 'PUT', body: data }),
  adminSetCityActive: (id, active) =>
    request(`/api/v1/admin/masters/cities/${id}/active`, { method: 'PATCH', body: { active } }),

  adminListBoards: () => request('/api/v1/admin/masters/boards'),
  adminCreateBoard: (data) => request('/api/v1/admin/masters/boards', { method: 'POST', body: data }),
  adminUpdateBoard: (id, data) =>
    request(`/api/v1/admin/masters/boards/${id}`, { method: 'PUT', body: data }),
  adminSetBoardActive: (id, active) =>
    request(`/api/v1/admin/masters/boards/${id}/active`, { method: 'PATCH', body: { active } }),

  adminListDesignations: () => request('/api/v1/admin/masters/designations'),
  adminCreateDesignation: (data) =>
    request('/api/v1/admin/masters/designations', { method: 'POST', body: data }),
  adminUpdateDesignation: (id, data) =>
    request(`/api/v1/admin/masters/designations/${id}`, { method: 'PUT', body: data }),
  adminSetDesignationActive: (id, active) =>
    request(`/api/v1/admin/masters/designations/${id}/active`, { method: 'PATCH', body: { active } }),

  adminListClassLevels: () => request('/api/v1/admin/masters/class-levels'),
  adminCreateClassLevel: (data) =>
    request('/api/v1/admin/masters/class-levels', { method: 'POST', body: data }),
  adminUpdateClassLevel: (id, data) =>
    request(`/api/v1/admin/masters/class-levels/${id}`, { method: 'PUT', body: data }),
  adminSetClassLevelActive: (id, active) =>
    request(`/api/v1/admin/masters/class-levels/${id}/active`, { method: 'PATCH', body: { active } }),

  adminListSubjects: () => request('/api/v1/admin/masters/subjects'),
  adminCreateSubject: (data) => request('/api/v1/admin/masters/subjects', { method: 'POST', body: data }),
  adminUpdateSubject: (id, data) =>
    request(`/api/v1/admin/masters/subjects/${id}`, { method: 'PUT', body: data }),
  adminSetSubjectActive: (id, active) =>
    request(`/api/v1/admin/masters/subjects/${id}/active`, { method: 'PATCH', body: { active } }),

  adminListUsers: (params) => request(`/api/v1/admin/users?${query(params)}`),
  adminCreateUser: (data) => request('/api/v1/admin/users', { method: 'POST', body: data }),
  adminUpdateUser: (id, data) => request(`/api/v1/admin/users/${id}`, { method: 'PUT', body: data }),
  // Blank password means "generate one and email it". Answers with the account and,
  // only where the environment exposes generated credentials, the password itself.
  adminResetPassword: (id, password) =>
    request(`/api/v1/admin/users/${id}/password/reset`, {
      method: 'POST',
      body: { password: password || null },
    }),
  adminUpdateStatus: (id, status) =>
    request(`/api/v1/admin/users/${id}/status`, { method: 'PATCH', body: { status } }),
  // Replaces the whole territory, so the caller sends every state the rep should have.
  adminUpdateTerritory: (id, stateIds) =>
    request(`/api/v1/admin/users/${id}/territory`, { method: 'PUT', body: { stateIds } }),
  /**
   * A teacher created from Registered Users. Multipart and the same payload shape as
   * self-registration, because it is the same form — the ID proof is optional here,
   * since the person filling it in does not hold the document.
   */
  adminCreateTeacher: (payload, idProofFile) => {
    const form = new FormData();
    form.append('payload', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
    if (idProofFile) form.append('idProof', idProofFile);
    return request('/api/v1/admin/teachers', { method: 'POST', formData: form });
  },
};
